import type { ClubCategory } from '@golf/common';
import { api } from './api.ts';

export interface Club {
    id: string;
    category: ClubCategory;
    /** Le modèle du catalogue dont ce club relève, s'il en relève d'un.
     *  C'est ce que désignent les règles de club d'un exercice. */
    templateId: number | null;
    label: string;
    nickname: string | null;
    brand: string | null;
    model: string | null;
    loftDeg: number | null;
    lieAngleDeg: number | null;
    shaftFlex: string | null;
    referenceDistanceM: number | null;
    purchasePrice: number | null;
    purchaseCurrency: string;
    acquiredOn: string | null;
    notes: string | null;
    bags: { id: string; name: string }[];
    shotCount: number;
    /** Coups centrés, toute période confondue — de quoi calculer la
     *  fiabilité du club sans redemander une fenêtre glissante. */
    centerCount: number;
}

export interface Bag {
    id: string;
    name: string;
    description: string | null;
    isActive: boolean;
    clubs: {
        id: string; label: string; nickname: string | null;
        category: ClubCategory; brand: string | null; model: string | null;
        loftDeg: number | null; position: number;
        /** Le type de club dont il relève — « fer 9 », « pitching ». C'est sur
         *  ce numéro que porte la liste de clubs permis d'un exercice, jamais
         *  sur le club lui-même : l'exercice doit rester valable après un
         *  changement de matériel. `null` sur un club saisi à la main. */
        templateId: number | null;
        /** Distance de référence saisie sur le club, en mètres. */
        referenceDistanceM: number | null;
        /** Moyenne réellement atteinte par le joueur avec ce club, en mètres. */
        avgDistanceM: number | null;
        /** Balles frappées avec ce club, toutes séances confondues. */
        shots: number;
        /** Celles qui sont parties droit. Le rapport des deux est la fiabilité
         *  affichée sur la carte du club — la même qu'au parcours. */
        center: number;
    }[];
}

export interface ClubTemplate {
    id: number;
    category: ClubCategory;
    code: string;
    label: string;
    defaultLoft: number | null;
    typicalCarryM: number | null;
}

export interface ClubSeries {
    id: number;
    code: string;
    brand: string;
    name: string;
    variantLabel: string | null;
    description: string | null;
    /** La série se vend en plusieurs tailles (Inesis) : la demander à l'ajout. */
    sizesOffered: boolean;
    /** Rigidités proposées par le fabricant, en lettres normalisées. */
    flexes: string[];
    items: {
        position: number;
        category: ClubCategory;
        label: string;
        loftDeg: number | null;
        lieAngleDeg: number | null;
    }[];
}

export type ClubDraft = Omit<Club, 'id' | 'bags' | 'shotCount' | 'centerCount'>;

export const listClubs = () => api<{ clubs: Club[] }>('/equipment/clubs').then((r) => r.clubs);
export const listBags = () => api<{ bags: Bag[] }>('/equipment/bags').then((r) => r.bags);
export const listTemplates = () =>
    api<{ templates: ClubTemplate[] }>('/equipment/templates').then((r) => r.templates);

export const listSeries = () =>
    api<{ series: ClubSeries[] }>('/equipment/series').then((r) => r.series);

export const addSeries = (id: number, body: { shaftFlex?: string | null; size?: '1' | '2' | null }) =>
    api<{ added: number; skipped: number }>(`/equipment/series/${id}/add`, { method: 'POST', body });

export const createClub = (body: Partial<ClubDraft>) =>
    api<{ club: Club }>('/equipment/clubs', { method: 'POST', body });
export const updateClub = (id: string, body: Partial<ClubDraft>) =>
    api<{ club: Club }>(`/equipment/clubs/${id}`, { method: 'PATCH', body });
export const archiveClub = (id: string) =>
    api<void>(`/equipment/clubs/${id}`, { method: 'DELETE' });

export const createBag = (body: { name: string; description?: string | null }) =>
    api<{ bag: Bag }>('/equipment/bags', { method: 'POST', body });
export const updateBag = (id: string, body: { name?: string; description?: string | null }) =>
    api<{ bag: Bag }>(`/equipment/bags/${id}`, { method: 'PATCH', body });
export const activateBag = (id: string) =>
    api(`/equipment/bags/${id}/activate`, { method: 'POST' });
export const archiveBag = (id: string) =>
    api<void>(`/equipment/bags/${id}`, { method: 'DELETE' });
export const setBagClubs = (id: string, clubIds: string[]) =>
    api(`/equipment/bags/${id}/clubs`, { method: 'PUT', body: { clubIds } });

export interface ClubIdentity {
    label: string;
    brand?: string | null;
    loftDeg?: number | null | string;
    nickname?: string | null;
}

/** Libellé complet, tout sur une ligne : « Fer 7 · Inesis 33° (le vieux) ».
 *
 *  Réservé à MON MATÉRIEL et aux chaînes qui ne peuvent pas porter de mise en
 *  forme — noms accessibles, titres au survol. Partout ailleurs, c'est le
 *  composant ClubLabel qui rend le type et son qualificatif séparément. */
export function clubDisplayLabel(c: ClubIdentity): string {
    const q = clubQualifier(c);
    return q ? `${c.label} ${q}` : c.label;
}

/** Ce qui distingue deux clubs du même type, et rien d'autre.
 *
 *  Le nom du type ne suffit pas TOUJOURS : un joueur peut posséder deux fers 7
 *  de marques et d'angles différents, et ce sont deux clubs distincts avec
 *  deux séries de statistiques. Mais c'est le cas rare, et l'angle réel n'est
 *  presque jamais ce qu'on cherche à lire : on cherche « le fer 7 ». D'où la
 *  séparation — le type porte l'identité, le qualificatif ne sert qu'à
 *  départager, et il se rend en retrait.
 *
 *  `null` quand il n'y a rien à départager : afficher un séparateur orphelin
 *  serait pire que de ne rien afficher. */
export function clubQualifier(c: ClubIdentity): string | null {
    const parts: string[] = [];
    if (c.brand) parts.push(c.brand);
    if (c.loftDeg != null && c.loftDeg !== '') {
        parts.push(`${String(c.loftDeg).replace(/\.0$/, '')}°`);
    }
    if (c.nickname) parts.push(`(${c.nickname})`);
    return parts.length > 0 ? parts.join(' ') : null;
}
